/*
 * This service is to be used primarily by the AuthGuard.
 * It maintains an array of modules available to the user.
 * Whenever a user is changed (impersonated), you must call setAccess() (this happens in the UserManagementService).
 * To see if a user has access to a module, call hasAccessToPage() and pass the new router URL.
 */

import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { IModuleSubComponents } from '../interfaces/module-card-info';
import { ActivatedRouteSnapshot, Router } from '@angular/router';

@Injectable({
    providedIn: 'root',
})
export class UserAccessService {
    private serverUrl = 'https://' + window.location.host;
    private version = '0.5';
    private _url = (this.serverUrl + '/api/v{version}/directoryaccess').replace(
        '{version}',
        this.version
    );

    private exclusions: any[] = [
        { name: 'Reports', path: '/report-menu' },
        { name: 'Home', path: '/home' },
        { name: 'settings', path: '/settings' },
        { name: 'settings-user-profile', path: '/settings/user-profile' },
        { name: 'settings-notifications', path: '/settings/notifications' },
        { name: 'scheduled-reports', path: '/settings/scheduled-reports' },
        { name: 'user-subscriptions', path: '/settings/user-subscriptions' },
        { name: 'eclose-roi-settings', path: '/settings/eclose-roi-settings' },
    ];

    public dataUpdatedSubject = new BehaviorSubject({
        valueChanged: false,
        shouldReloadPage: true,
    });

    constructor(private http: HttpClient, private router: Router) {}

    get data() {
        let d: UserAccessModule[];
        d = JSON.parse(localStorage.getItem('userAccessModules'));
        if (d) {
            return d;
        } else {
            return [];
        }
    }

    public setAccess(shouldReloadPage: boolean = true) {
        this.getData().subscribe((data) => {
            localStorage.setItem('userAccessModules', JSON.stringify(data));

            this.dataUpdatedSubject.next({
                valueChanged: true,
                shouldReloadPage: shouldReloadPage,
            });
        });
    }

    private getData(): Observable<UserAccessModule[]> {
        return this.http.get<UserAccessModule[]>(`${this._url}/user`);
    }

    public hasAccessToPage(routerURL: string, route?: ActivatedRouteSnapshot): boolean {
        let hasModuleAccess = false;
        routerURL = routerURL.toString().replace('%20', ' ');

        if (route) {
            let params = route.params;
            if (Object.keys(route.params).length > 0) {
                for (let prop in params) {
                    routerURL = routerURL.replace(`/${params[prop]}`, '');
                }
            }
        }

        routerURL = routerURL.toString().replace(/\?(.*)/, '');
        routerURL = routerURL.replace(/\((.*)/, '');

        // Check for exclusions to access rules:
        let filteredExclusions = this.exclusions.filter((x) => x.path == routerURL);
        if (filteredExclusions && filteredExclusions.length > 0) {
            hasModuleAccess = true;
        }

        // If exclusions did not apply, check route against module access data:
        if (!hasModuleAccess) {
            let d: UserAccessModule[];
            d = this.data;
            if (d && d.length > 0) {
                hasModuleAccess = this.checkAccessFilter(d, routerURL);
            } else {
                hasModuleAccess = false;
            }
        }

        // DEBUG
        // console.log(routerURL, hasModuleAccess);

        return hasModuleAccess;
    }

    public updateModuleLockValues(modules: IModuleSubComponents[]) {
        modules.forEach((m) => {
            try {
                m.moduleLocked = !this.hasAccessToPage(`/${m.moduleLink}`);
            } catch (e) {
                m.moduleLocked = true;
            }
        });

        return modules;
    }

    private checkAccessFilter(d: UserAccessModule[], routerURL: string): boolean {
        let filteredResult = null;

        filteredResult = d.filter((x) => routerURL === x.routerURL && x.isLocked === false);

        let result = filteredResult && filteredResult.length > 0;

        if (result) {
            result = !filteredResult[0].isLocked; // Opposite of 'isLocked' value
        }
        return result;
    }

    public navigateToHomePage() {
        this.http.get<any>(`${this._url}/user/home`).subscribe(
            (res) => {
                let homePageKey = res.homePageKey;

                if (homePageKey == 0) {
                    this.router.navigate(['/home']);
                } else {
                    let routerUrl = this.data.find(
                        (x) => x.applicationPageKey === homePageKey
                    )?.routerURL;

                    if (
                        routerUrl == null ||
                        routerUrl == '/home' ||
                        !this.hasAccessToPage(routerUrl)
                    ) {
                        this.router.navigate(['/home']);
                    } else {
                        this.router.navigate([routerUrl]);
                    }
                }
            },
            (error) => {
                this.router.navigate(['/home']);
            }
        );
    }
}

export interface UserAccessModule {
    applicationPageKey: number;
    routerURL: string;
    name: string;
    isLocked: boolean;
}
